import { HideContainer } from '@components/hide-container';
import { IconSend } from '@components/icons/send';
import { Spinner } from '@components/loading-spinner';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import { AttributeFieldType } from 'api/attributes/requests';
import { createTravellerJourneyActionFieldPath } from 'api/documents/requests';
import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { JourneyField, TravellerJourneyActionDetailed } from 'api/types';
import { useUsers } from 'api/users/hooks';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import { UpdateFieldsFormContent } from './helpers';

import {
  ActionProgressBar,
  EditableActionContent,
  RenderAttributeFieldLabelGroup,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  TravellerActionHeader,
  isActionError,
  useEditingContext,
} from '../common';

const ACTION_TYPE = ACTION_TYPES.update_traveller;

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);

  const travellerActionQuery = useJourneyTravellerAction<typeof ACTION_TYPE>(shortId);
  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }
  const action = travellerActionQuery.data;

  return (
    <Sidebar>
      <TravellerActionHeader actionType={ACTION_TYPE}>
        <TravellerActionActions
          action={action}
          ratioOverride={action.status === 'completed' ? 1 : 0}
          statusOverride={action.status}
        />
      </TravellerActionHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={<FormContent action={action} />}
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const status = action.status;
  const isError = isActionError(action);

  const fields = (action.data.fields ?? []) as JourneyField[];
  const updatedFields = (action.data.updatedFields ?? []) as {
    field: JourneyField;
    fieldName: string;
    fieldTitle: string;
    fieldType: AttributeFieldType;
    optionValue: string;
  }[];

  const attributeTemplatesQuery = useCompanyTravellerAttributeTemplates({
    queryData: { ordering: 'title' },
    systemAttributes: true,
  });
  const attributes = attributeTemplatesQuery.data ?? [];
  const userQuery = useUsers();
  const usersList = userQuery.data;

  return (
    <Stack space={6}>
      <HideContainer title={t('actionStatus')}>
        <ActionProgressBar
          action={action}
          isError={isError}
          errorMessage={
            isError
              ? {
                  title: actionErrorTitle[action.action],
                  description: action.result?.message,
                  date: action.result?.dateFailed,
                }
              : undefined
          }
          dateSuccess={action.result?.dateSucceeded}
        />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('fields')}>
        <Stack space={4}>
          {fields.map((field, idx) => (
            <RenderAttributeFieldLabelGroup
              key={idx}
              attribute={attributes.find((attribute) => attribute.shortId === field.field)}
              field={field}
              usersList={usersList}
              savedFieldName={
                updatedFields.find((ufield) => ufield.field.field === field.field)?.fieldTitle
              }
              savedFieldType={
                updatedFields.find((ufield) => ufield.field.field === field.field)?.fieldType ??
                field.last_saved_type
              }
              savedOptionValue={
                updatedFields.find((ufield) => ufield.field.field === field.field)?.optionValue
              }
              actionStatus={status}
            />
          ))}
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      {status !== 'completed' && (
        <TravellerActionFooter
          action={action}
          startIcon={<IconSend />}
          startText={t('run')}
          retryText={t('retry')}
        />
      )}
    </Stack>
  );
};

const FormContent = ({ action }: ContentProps) => {
  const { stopEditing } = useEditingContext();

  const editMutation = useUpdateJourneyTravellerAction(action, { onSuccess: stopEditing });

  const filePath = createTravellerJourneyActionFieldPath(action);

  return (
    <UpdateFieldsFormContent
      action={action}
      journeyId={action.journeyId}
      filePath={filePath}
      onCancel={stopEditing}
      onSubmit={(data) =>
        editMutation.mutateAsync({
          isAutomatic: true,
          action: 'update_traveller',
          values: { fields: data.fields },
          description: data.description,
        })
      }
    />
  );
};

const sidebarLocale = {
  en: {
    dataNotFound: 'Data not found',
    actionStatus: 'Action status',
    fields: 'Fields',
    run: 'Run',
    retry: 'Retry',
  },
};
